import * as DialogPrimitive from '@radix-ui/react-dialog';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Heart, MessageCircle, X, Send, ChevronLeft, ChevronRight, ArrowLeft, FolderOpen, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import useReveal from '@/hooks/useReveal';
import useViewerMotion from '@/hooks/useViewerMotion';
import PhotoImage from '@/components/PhotoImage';
import { imageUrl, photoSource } from '@/lib/photoSource';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PAGE_SIZE = 24;
const viewerSource = photo => imageUrl(photo.image_url, 1920);

// Navigation stays responsive while the previous image remains visible until decode completes.
const decodePhoto = (photo) => {
  return new Promise(resolve => {
    const image = new Image();
    image.src = viewerSource(photo);
    image.decode().then(resolve, resolve);
  });
};

// Generate a cryptographically random visitor ID for likes
const getVisitorId = () => {
  let id = localStorage.getItem('ferdia-visitor-id');
  if (!id) {
    id = 'visitor_' + (crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substr(2, 16));
    localStorage.setItem('ferdia-visitor-id', id);
  }
  return id;
};

const Gallery = () => {
  const { getAuthHeaders, user, fingerprintReady } = useAuth();
  const [albums, setAlbums] = useState([]);
  const [categories] = useState(['all', 'street', 'landscape', 'sport', 'wildlife', 'other']);
  const [activeCategory, setActiveCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const [commentsEnabled, setCommentsEnabled] = useState(false);
  const [albumLimit, setAlbumLimit] = useState(PAGE_SIZE);

  const [albumEntrance, setAlbumEntrance] = useState(false);
  const [photoEntrance, setPhotoEntrance] = useState(false);
  const [collectionMotion, setCollectionMotion] = useState(true);
  const albumRequest = useRef(null);
  useEffect(() => () => albumRequest.current?.abort(), []);
  useEffect(() => {
    const controller = new AbortController();
    const loadCommentSettings = async () => {
      try {
        const { data } = await axios.get(`${API}/comments/config`, { signal: controller.signal });
        if (!controller.signal.aborted) setCommentsEnabled(data.enabled === true);
      } catch {
        if (!controller.signal.aborted) setCommentsEnabled(false);
      }
    };
    loadCommentSettings();
    window.addEventListener('focus', loadCommentSettings);
    return () => { controller.abort(); window.removeEventListener('focus', loadCommentSettings); };
  }, []);

  // Album modal state
  const [selectedAlbum, setSelectedAlbum] = useState(null);
  const [albumPhotos, setAlbumPhotos] = useState([]);
  const [albumLoading, setAlbumLoading] = useState(false);
  const [albumHasMore, setAlbumHasMore] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);
  const pageRequest = useRef(null);

  // Photo modal state
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const intendedIndex = useRef(-1);
  const navigationRequest = useRef(0);
  const [navigationPending, setNavigationPending] = useState(false);

  const [error, setError] = useState(false);
  const gridReveal = useReveal();
  const heroContent = useRef(null);
  const heroCue = useRef(null);
  useEffect(() => {
    const nav = document.querySelector('.nav-cinema--home');
    if (!nav || !heroContent.current || !heroCue.current) return undefined;
    const elements = [nav, heroContent.current, heroCue.current];
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let frame = 0;
    const setOpacity = (element, opacity) => {
      element.style.opacity = opacity;
      const hidden = opacity === 0;
      if (element.inert !== hidden) {
        element.style.visibility = hidden ? 'hidden' : '';
        element.inert = hidden;
      }
    };
    const update = () => {
      frame = 0;
      const height = window.innerHeight;
      const scroll = window.scrollY;
      const keyboardFocusInNav = document.documentElement.dataset.input === 'keyboard' && nav.contains(document.activeElement);
      const navOpacity = keyboardFocusInNav ? 1 : motion.matches ? Number(scroll < height * .32) : Math.max(0, Math.min(1, (height * .32 - scroll) / (height * .22)));
      const heroOpacity = motion.matches ? Number(scroll < height * .5) : Math.max(0, Math.min(1, (height * .5 - scroll) / (height * .35)));
      setOpacity(nav, navOpacity);
      nav.style.transform = motion.matches ? '' : `translateY(${-48 * (1 - navOpacity)}px)`;
      setOpacity(heroContent.current, heroOpacity);
      setOpacity(heroCue.current, heroOpacity);
    };
    const schedule = () => { if (!frame) frame = requestAnimationFrame(update); };
    update();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    nav.addEventListener('focusin', schedule);
    nav.addEventListener('focusout', schedule);
    motion.addEventListener('change', schedule);
    return () => {
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      nav.removeEventListener('focusin', schedule);
      nav.removeEventListener('focusout', schedule);
      motion.removeEventListener('change', schedule);
      cancelAnimationFrame(frame);
      elements.forEach(element => { element.style.opacity = ''; element.style.visibility = ''; element.style.transform = ''; element.inert = false; });
    };
  }, []);

  const fetchAlbums = useCallback(async (signal) => {
    setLoading(true);
    setError(false);
    try {
      const params = activeCategory !== 'all' ? `?category=${activeCategory}` : '';
      const response = await axios.get(`${API}/albums${params}`, { headers: getAuthHeaders(), signal });
      setAlbums(response.data);
      setAlbumLimit(PAGE_SIZE);
    } catch (error) {
      if (!signal?.aborted) setError(true);
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [activeCategory, getAuthHeaders]);

  useEffect(() => {
    if (fingerprintReady || user) {
      const controller = new AbortController();
      fetchAlbums(controller.signal);
      return () => controller.abort();
    }
  }, [fetchAlbums, fingerprintReady, user]);

  const openAlbum = async (album, event) => {
    albumRequest.current?.abort();
    const controller = new AbortController();
    albumRequest.current = controller;
    pageRequest.current = null;
    setPageLoading(false);
    setAlbumHasMore(false);
    setAlbumEntrance(event?.detail > 0);
    setAlbumPhotos([]);
    setSelectedAlbum(album);
    setAlbumLoading(true);
    try {
      const response = await axios.get(`${API}/photos?album_id=${encodeURIComponent(album.id)}&limit=${PAGE_SIZE}&offset=0`, { headers: getAuthHeaders(), signal: controller.signal });
      if (!controller.signal.aborted) {
        setAlbumPhotos(response.data);
        setAlbumHasMore(response.data.length === PAGE_SIZE);
      }
    } catch (error) {
      if (!controller.signal.aborted) toast.error('Failed to load photos');
    } finally {
      if (!controller.signal.aborted) setAlbumLoading(false);
    }
  };

  const loadMorePhotos = useCallback(() => {
    if (pageRequest.current) return pageRequest.current;
    const controller = albumRequest.current;
    if (!selectedAlbum || !albumHasMore || controller?.signal.aborted) return Promise.resolve([]);
    setPageLoading(true);
    const request = axios.get(`${API}/photos?album_id=${encodeURIComponent(selectedAlbum.id)}&limit=${PAGE_SIZE}&offset=${albumPhotos.length}`, { headers: getAuthHeaders(), signal: controller.signal })
      .then(({ data }) => {
        if (controller.signal.aborted) return [];
        setAlbumPhotos(current => [...current, ...data]);
        setAlbumHasMore(data.length === PAGE_SIZE);
        return data;
      }).catch(() => {
        if (!controller.signal.aborted) toast.error('Could not load more photos. Please try again.');
        return [];
      }).finally(() => {
        if (pageRequest.current === request) { pageRequest.current = null; setPageLoading(false); }
      });
    pageRequest.current = request;
    return request;
  }, [selectedAlbum, albumHasMore, albumPhotos.length, getAuthHeaders]);

  const closeAlbum = () => {
    navigationRequest.current++;
    albumRequest.current?.abort();
    setSelectedAlbum(null);
    setAlbumPhotos([]);
    setSelectedPhoto(null);
    setSelectedIndex(-1);
  };

  const openPhotoModal = (photo, index, event) => {
    navigationRequest.current++;
    intendedIndex.current = index;
    setNavigationPending(false);
    setPhotoEntrance(event?.detail > 0);
    setSelectedPhoto(photo);
    setSelectedIndex(index);
  };

  const closePhotoModal = () => {
    navigationRequest.current++;
    intendedIndex.current = -1;
    setNavigationPending(false);
    setSelectedPhoto(null);
    setSelectedIndex(-1);
  };

  const navigatePhoto = useCallback(async (direction, event) => {
    let newIndex = intendedIndex.current + direction;
    if (newIndex < 0 || (!albumHasMore && newIndex >= albumPhotos.length)) return;
    if (newIndex >= albumPhotos.length + PAGE_SIZE) return;
    intendedIndex.current = newIndex;
    const request = ++navigationRequest.current;
    setNavigationPending(true);
    let next = albumPhotos[newIndex];
    if (!next) {
      const added = await loadMorePhotos();
      if (added.length && added.length < PAGE_SIZE) newIndex = Math.min(newIndex, albumPhotos.length + added.length - 1);
      next = added[newIndex - albumPhotos.length];
    }
    if (request !== navigationRequest.current) return;
    setNavigationPending(false);
    if (!next) { intendedIndex.current = selectedIndex; return; }
    intendedIndex.current = newIndex;
    setSelectedPhoto(next);
    setSelectedIndex(newIndex);
  }, [selectedIndex, albumPhotos, albumHasMore, loadMorePhotos]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        navigationRequest.current++;
        if (selectedPhoto) { setSelectedPhoto(null); setSelectedIndex(-1); intendedIndex.current = -1; setNavigationPending(false); }
        else if (selectedAlbum) { albumRequest.current?.abort(); setSelectedAlbum(null); setAlbumPhotos([]); }
        return;
      }
      if (selectedPhoto && !['INPUT', 'TEXTAREA'].includes(e.target.tagName)) {
        if (e.key === 'ArrowLeft') navigatePhoto(-1);
        if (e.key === 'ArrowRight') navigatePhoto(1);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedPhoto, selectedAlbum, selectedIndex, albumPhotos.length, navigatePhoto]);

  const filteredAlbums = albums.filter(album =>
    (activeCategory === 'all' || album.category === activeCategory) && album.photo_count > 0
  );

  return (
    <div className="min-h-screen relative">

      {/* One sharp photo stays behind the entire page. */}
      <section id="hero" className="relative min-h-[92vh] flex items-center justify-center overflow-hidden">
        <div ref={heroContent} className="relative z-10 text-center px-4 max-w-5xl mx-auto">
          <p className="editorial-eyebrow text-xs md:text-sm uppercase text-white/90 mb-6 text-contrast">
            ferdia's gallery
          </p>
          <h1
            className="text-5xl md:text-7xl lg:text-8xl font-light italic leading-[0.95] mb-8 text-white text-contrast-strong"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            Moments,<br />Captured Forever
          </h1>
          <p className="text-base md:text-lg text-white/90 font-light max-w-2xl mx-auto leading-relaxed text-contrast">
            A collection of wildlife, landscapes, and the energy of sport.
            Enjoy my work — and if anything catches your eye, let's talk.
          </p>

          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <a href="#collection" className="btn-ghost btn-ghost-fill" data-testid="hero-cta-browse">
              Enter the Gallery
            </a>
            <Link to="/contact" className="btn-ghost" data-testid="hero-cta-contact">
              Enquire to Purchase
            </Link>
          </div>
        </div>

        <a ref={heroCue} href="#collection" aria-label="Scroll to collection" className="scroll-cue">
          <ChevronDown className="w-6 h-6" />
        </a>
      </section>

      {/* ====================== CATEGORY FILTERS (borderless) ====================== */}
      <section
        id="collection"
        className="relative z-40 py-4"
      >
        <div className="max-w-[1600px] mx-auto px-4 md:px-8 lg:px-12">
          <div className="flex items-center">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="filter-pill flex items-center gap-2"
                  data-testid="category-filter-trigger"
                >
                  {activeCategory === 'all' ? 'All' : activeCategory}
                  <ChevronDown className="w-4 h-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="rounded-xl capitalize">
                <DropdownMenuRadioGroup value={activeCategory} onValueChange={(category) => { setCollectionMotion(document.documentElement.dataset.input !== "keyboard"); setActiveCategory(category); }}>
                  {categories.map((category) => (
                    <DropdownMenuRadioItem
                      key={category}
                      value={category}
                      className="cursor-pointer rounded-lg capitalize"
                      data-testid={`filter-${category}`}
                    >
                      {category}
                    </DropdownMenuRadioItem>
                  ))}
                </DropdownMenuRadioGroup>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </section>

      {/* ====================== ALBUMS GRID (liquid-glass tiles) ====================== */}
      <section className="relative z-10 max-w-[1600px] mx-auto px-4 md:px-8 lg:px-12 py-16 md:py-24">
        <div ref={gridReveal} className="reveal mb-6 md:mb-8 max-w-2xl">
          <h2
            className="text-4xl md:text-5xl font-normal tracking-tight text-on-photo-strong"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            Albums
          </h2>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="spinner rounded-full h-12 w-12 border-b-2 border-foreground" role="status" aria-label="Loading photos"></div>
          </div>
        ) : error ? (
          <div role="alert" className="text-center py-16"><h3 className="text-2xl mb-3">The collection could not load</h3><p className="text-muted-foreground mb-6">Please check your connection and try again.</p><Button onClick={() => fetchAlbums()}>Try again</Button></div>
        ) : filteredAlbums.length === 0 ? (
          <div className="text-center py-20 text-on-photo">
            <FolderOpen className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <p className="text-xl text-on-photo">No albums in this category yet.</p>
            <p className="text-sm text-muted-foreground mt-2">Check back soon for new uploads!</p>
          </div>
        ) : (
          <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 md:gap-8 ${collectionMotion ? "collection-loaded" : ""}`}>
            {filteredAlbums.slice(0, albumLimit).map((album) => (
              <AlbumCard
                key={album.id}
                album={album}
                onClick={(event) => openAlbum(album, event)}
              />
            ))}
          </div>
        )}
        {!loading && !error && filteredAlbums.length > PAGE_SIZE && <div className="text-center mt-8 space-y-3">
          <p className="text-sm text-on-photo-muted" role="status">Showing {Math.min(albumLimit, filteredAlbums.length)} of {filteredAlbums.length} albums</p>
          {albumLimit < filteredAlbums.length && <Button onClick={() => {
            const nextId = filteredAlbums[albumLimit].id;
            setAlbumLimit(limit => limit + PAGE_SIZE);
            requestAnimationFrame(() => document.querySelector(`[data-testid="album-card-${CSS.escape(nextId)}"]`)?.focus({ preventScroll: true }));
          }}>Load more albums</Button>}
        </div>}
      </section>

      {/* ====================== ALBUM MODAL (borderless) ====================== */}
      {selectedAlbum && (
        <AlbumModal
          entrance={albumEntrance}
          album={selectedAlbum}
          photos={albumPhotos}
          loading={albumLoading}
          onClose={closeAlbum}
          onPhotoClick={openPhotoModal}
          hasMore={albumHasMore}
          pageLoading={pageLoading}
          onLoadMore={loadMorePhotos}
          commentsEnabled={commentsEnabled}
        />
      )}

      {/* ====================== PHOTO MODAL (borderless) ====================== */}
      {selectedPhoto && (
        <PhotoModal
          entrance={photoEntrance}
          photo={selectedPhoto}
          onClose={closePhotoModal}
          onNavigate={navigatePhoto}
          pending={navigationPending}
          adjacentPhotos={albumPhotos.slice(Math.max(0, selectedIndex - 1), selectedIndex + 2)}
          hasNext={intendedIndex.current < albumPhotos.length - 1 || albumHasMore}
          hasPrev={intendedIndex.current > 0}
          commentsEnabled={commentsEnabled}
        />
      )}

      {/* ====================== FOOTER ====================== */}
      <SiteFooter />
    </div>
  );
};

// ============== ALBUM CARD (liquid-glass tile) ==============

const AlbumCard = ({ album, onClick }) => {
  return (
    <div
      className="group cursor-pointer"
      role="button" tabIndex={0} aria-label={`Open album: ${album.title}`}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(e); } }}
      onClick={onClick}
      data-testid={`album-card-${album.id}`}
    >
      <div className="liquid-glass aspect-[4/3] overflow-hidden">
        {album.cover_photo ? (
          <PhotoImage
            {...photoSource({ image_url: album.cover_photo }, '(min-width: 1280px) 360px, (min-width: 640px) 45vw, 90vw')}
            alt={album.title}
            className="album-cover w-full h-full object-cover protected-image"
            onContextMenu={(e) => e.preventDefault()}
            draggable={false}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <FolderOpen className="w-16 h-16 text-white/40" />
          </div>
        )}
        {/* Soft gradient at the bottom for the caption */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent pointer-events-none" />

        {/* Caption */}
        <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
          <span className="inline-block mb-3 text-[0.65rem] uppercase tracking-[0.2em] text-white/90 text-contrast-soft">
            {album.category}
          </span>
          <h3
            className="text-2xl md:text-3xl font-light leading-tight text-contrast"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            {album.title}
          </h3>
          <p className="text-sm text-white/85 mt-1.5 text-contrast-soft">
            {album.photo_count} {album.photo_count === 1 ? 'photo' : 'photos'}
          </p>
        </div>
      </div>
    </div>
  );
};

// ============== ALBUM MODAL (borderless, full-bleed) ==============

const AlbumModal = ({ album, photos, loading, onClose, onPhotoClick, entrance, hasMore, pageLoading, onLoadMore, commentsEnabled }) => {
  const { ref, close } = useViewerMotion(onClose, entrance);
  const opener = useRef(document.activeElement);
  return (
    <DialogPrimitive.Root open onOpenChange={(open) => !open && onClose()}>
    <DialogPrimitive.Portal><DialogPrimitive.Content asChild onEscapeKeyDown={(e) => e.preventDefault()} aria-label={album.title} aria-describedby={undefined} onCloseAutoFocus={(e) => { e.preventDefault(); opener.current?.focus(); }}>
    <div
      ref={ref}
      className="fixed inset-0 z-50 overflow-y-auto"
      style={{ background: 'rgba(8,16,23,0.82)' }}
      data-testid="album-modal"
      data-lenis-prevent
    >
      {/* Header (text only, no bar) */}
      <div className="sticky top-0 z-10 px-4 md:px-8 lg:px-12 py-4 flex items-center gap-4"
           style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.55), transparent)' }}>
        <button
          onClick={close}
          className="icon-btn flex items-center gap-2 text-sm text-white/85 hover:text-white transition-colors"
          aria-label="Back to gallery" data-testid="close-album"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="hidden sm:inline">Back to Gallery</span>
        </button>

        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="text-[0.65rem] uppercase tracking-[0.2em] text-white/85 text-contrast-soft shrink-0">
              {album.category}
            </span>
            <DialogPrimitive.Title asChild><h2
              className="text-2xl md:text-3xl font-light truncate text-white text-contrast"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              {album.title}
            </h2></DialogPrimitive.Title>
          </div>
          {album.description && (
            <p className="text-sm text-white/75 mt-0.5 truncate text-contrast-soft">{album.description}</p>
          )}
        </div>

        <span className="text-sm text-white/85 shrink-0 text-contrast-soft">
          {album.photo_count} {album.photo_count === 1 ? 'photo' : 'photos'}
        </span>
      </div>

      {/* Photos Grid */}
      <div className="max-w-[1600px] mx-auto px-4 md:px-8 lg:px-12 py-10">
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="spinner rounded-full h-12 w-12 border-b-2 border-white"></div>
          </div>
        ) : photos.length === 0 ? (
          <div className="text-center py-20 text-white">
            <p className="text-xl text-white/80 text-contrast">No photos in this album yet.</p>
          </div>
        ) : (
          <div className="masonry-grid">
            {photos.map((photo, index) => (
              <PhotoCard
                key={photo.id}
                photo={photo}
                onClick={(event) => onPhotoClick(photo, index, event)}
                commentsEnabled={commentsEnabled}
              />
            ))}
          </div>
        )}
        {hasMore && <div className="mt-8 text-center"><Button disabled={pageLoading} onClick={async () => {
          const added = await onLoadMore();
          if (added[0]) requestAnimationFrame(() => document.querySelector(`[data-testid="photo-card-${CSS.escape(added[0].id)}"]`)?.focus({ preventScroll: true }));
        }}>{pageLoading ? 'Loading photos…' : 'Load more photos'}</Button></div>}
      </div>
    </div>
    </DialogPrimitive.Content></DialogPrimitive.Portal></DialogPrimitive.Root>
  );
};

// ============== PHOTO CARD ==============

const PhotoCard = ({ photo, onClick, commentsEnabled }) => {
  const panoramic = photo.width > photo.height * 2;
  return (
    <div
      className="photo-tile masonry-item cursor-pointer group w-full"
      onClick={onClick}
      role="button" tabIndex={0} aria-label={`Open photo: ${photo.title}`}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(e); } }}
      data-testid={`photo-card-${photo.id}`}
    >
      <div className="photo-tile-frame relative overflow-hidden rounded-xl">
        <PhotoImage
          {...photoSource(photo, '(min-width: 1280px) 360px, (min-width: 1024px) 33vw, (min-width: 640px) 50vw, calc(100vw - 32px)')}
          alt={photo.title}
          className="w-full h-auto block protected-image"
          loading="lazy" decoding="async"
          onContextMenu={(e) => e.preventDefault()}
          draggable={false}
        />
        <div className={`photo-tile-caption absolute inset-x-0 bottom-0 text-white ${panoramic ? 'px-4 py-3' : 'p-5'}`}>
          <h3 className={`photo-tile-title font-medium text-contrast ${panoramic ? 'text-lg' : 'text-xl'}`} style={{ fontFamily: "'Cormorant Garamond', serif", WebkitLineClamp: panoramic ? 1 : 2 }}>
            {photo.title}
          </h3>
          <div className={`flex items-center gap-4 text-sm text-white/90 text-contrast-soft ${panoramic ? 'mt-1' : 'mt-2'}`}>
            <span className="flex items-center gap-1.5">
              <Heart className="w-4 h-4" /> {photo.likes_count}
            </span>
            {commentsEnabled && <span className="flex items-center gap-1.5">
              <MessageCircle className="w-4 h-4" /> {photo.comments_count}
            </span>}
          </div>
        </div>
      </div>
    </div>
  );
};

// ============== PHOTO MODAL (borderless, liquid-glass panel) ==============
// Activity requests are cancelled when navigating to another photo.

const PhotoModal = ({ photo, onClose, onNavigate, hasNext, hasPrev, entrance, pending, adjacentPhotos, commentsEnabled }) => {
  const { ref, close } = useViewerMotion(onClose, entrance);
  const { user, getAuthHeaders } = useAuth();
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [liked, setLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(photo.likes_count);
  const [loading, setLoading] = useState(false);
  const [displayedPhoto, setDisplayedPhoto] = useState(photo);
  const visitorId = getVisitorId();
  const opener = useRef(document.activeElement);
  const adjacentSources = adjacentPhotos.map(viewerSource).join('\n');
  useEffect(() => {
    let active = true;
    if (viewerSource(photo) !== viewerSource(displayedPhoto)) {
      decodePhoto(photo).then(() => { if (active) setDisplayedPhoto(photo); });
    }
    return () => { active = false; };
  }, [photo, displayedPhoto]);
  useEffect(() => {
    adjacentSources.split('\n').filter(Boolean).forEach(src => { const image = new Image(); image.src = src; image.decode().catch(() => {}); });
  }, [adjacentSources]);

  useEffect(() => {
    const controller = new AbortController();
    setComments([]);
    setNewComment('');
    setLiked(false);
    setLikesCount(photo.likes_count);
    const load = async () => {
      try {
        const [commentsResponse, likeResponse] = await Promise.all([
          commentsEnabled ? axios.get(`${API}/photos/${photo.id}/comments`, { headers: getAuthHeaders(), signal: controller.signal }) : Promise.resolve(null),
          axios.get(`${API}/photos/${photo.id}/like?visitor_id=${visitorId}`, { headers: getAuthHeaders(), signal: controller.signal })
        ]);
        if (controller.signal.aborted) return;
        if (commentsResponse) setComments(commentsResponse.data);
        setLiked(likeResponse.data.liked);
        setLikesCount(likeResponse.data.likes_count);
      } catch {
        if (!controller.signal.aborted) toast.error('Could not load photo activity. Please reopen the photo to try again.');
      }
    };
    load();
    return () => controller.abort();
  }, [photo.id, photo.likes_count, getAuthHeaders, visitorId, commentsEnabled]);

  const handleLike = async () => {
    try {
      const response = await axios.post(`${API}/photos/${photo.id}/like?visitor_id=${visitorId}`, {}, { headers: getAuthHeaders() });
      setLiked(response.data.liked);
      setLikesCount(response.data.likes_count);
    } catch (error) {
      toast.error('Failed to update like');
    }
  };

  const handleComment = async (e) => {
    e.preventDefault();
    if (!commentsEnabled) return;
    if (!newComment.trim()) {
      toast.error('Please write a comment');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/photos/${photo.id}/comments`,
        { content: newComment },
        { headers: getAuthHeaders() }
      );
      setComments([response.data, ...comments]);
      setNewComment('');
      toast.success('Comment added!');
    } catch (error) {
      if (!user) {
        toast.error('Please log in to add a comment');
      } else if (error.response?.status === 403) {
        toast.error(error.response?.data?.detail === 'Comments are currently disabled'
          ? 'Comments are currently disabled'
          : 'You have been banned from commenting. Contact business.ferdiakavanagh@gmail.com');
      } else {
        toast.error('Comment failed, please try again later');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <DialogPrimitive.Root open onOpenChange={(open) => !open && onClose()}>
    <DialogPrimitive.Portal><DialogPrimitive.Content asChild onEscapeKeyDown={(e) => e.preventDefault()} aria-label={photo.title} aria-describedby={undefined} onCloseAutoFocus={(e) => { e.preventDefault(); opener.current?.focus(); }}>
    <div
      ref={ref}
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      style={{ background: 'rgba(8,16,23,0.76)' }}
      onClick={(e) => e.target === e.currentTarget && close(e)}
      data-lenis-prevent
      data-testid="photo-modal"
    >
      {/* Close button */}
      <button
        onClick={close}
        className="absolute top-4 right-4 icon-btn text-white z-50"
        style={{ background: 'rgba(255,255,255,0.1)' }}
        aria-label="Close photo" data-testid="close-modal"
      >
        <X className="w-6 h-6" />
      </button>

      {/* Content */}
      <div className="photo-viewer-content max-w-6xl w-full flex flex-col lg:flex-row gap-6 overflow-hidden">
        {/* Image */}
        <div className="photo-viewer-image relative flex-1 flex items-center justify-center min-h-0 min-w-0 overflow-hidden" aria-busy={pending || displayedPhoto.id !== photo.id}>
          {hasPrev && <button onClick={(event) => onNavigate(-1, event)} className="photo-viewer-arrow left-2" aria-label="Previous photo" data-testid="prev-photo"><ChevronLeft className="w-6 h-6" /></button>}
          <PhotoImage
            src={viewerSource(displayedPhoto)}
            loading="eager" reveal={false} retry
            alt={displayedPhoto.title}
            className="max-w-full max-h-full object-contain protected-image"
            onContextMenu={(e) => e.preventDefault()}
            draggable={false}
          />
          {hasNext && <button onClick={(event) => onNavigate(1, event)} className="photo-viewer-arrow right-2" aria-label="Next photo" data-testid="next-photo"><ChevronRight className="w-6 h-6" /></button>}
          {(pending || displayedPhoto.id !== photo.id) && <p role="status" className="absolute bottom-2 rounded-full px-4 py-2 bg-black/60 text-white text-sm">Loading photo…</p>}
        </div>

        {/* Details Panel (liquid-glass, borderless) */}
        <div className={`photo-details liquid-glass w-full md:w-96 p-6 ${commentsEnabled ? '' : 'photo-details--compact'}`} data-lenis-prevent>
          <div className="photo-details-scroll" data-lenis-prevent>
          {/* Header */}
          <div className="photo-details-header mb-5 pb-5">
            <span className="text-[0.65rem] uppercase tracking-[0.2em] text-white/85 text-contrast-soft">
              {photo.category}
            </span>
            <DialogPrimitive.Title asChild><h2
              className="text-2xl font-medium text-white text-contrast mt-2"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              {photo.title}
            </h2></DialogPrimitive.Title>
            <a href={`/photos/${encodeURIComponent(photo.id)}`} className="text-sm text-white/85 underline inline-block mt-2">View photo page</a>
            {photo.description && (
              <p className="text-sm text-white/85 mt-2 text-contrast-soft">{photo.description}</p>
            )}
          </div>

          {/* Like Button */}
          <div className="photo-activity flex flex-wrap items-center gap-4 mb-5 p-3 rounded-xl">
            <button
              onClick={handleLike}
              className={`like-btn flex shrink-0 items-center gap-2 px-5 py-2.5 rounded-full transition-colors ${
                liked ? 'text-red-300 liked' : 'text-white/85 hover:text-white'
              }`}
              style={{ background: liked ? 'rgba(248,113,113,0.18)' : 'rgba(255,255,255,0.08)' }}
              aria-label={liked ? 'Unlike photo' : 'Like photo'} aria-pressed={liked}
              data-testid="like-btn"
            >
              <Heart className={`w-5 h-5 transition-transform ${liked ? 'fill-current scale-110' : ''}`} />
              <span className="font-medium text-contrast-soft whitespace-nowrap tabular-nums">{new Intl.NumberFormat().format(likesCount)}</span>
            </button>
            {commentsEnabled && <span className="flex items-center gap-2 text-white/85 text-contrast-soft">
              <MessageCircle className="w-5 h-5" />
              <span>{comments.length}</span>
            </span>}
          </div>

          {/* Comments */}
          {commentsEnabled && <div className="mb-5 space-y-3">
            <h3 className="text-sm font-medium text-white/90">Comments</h3>
            {comments.length === 0 ? (
              <p className="text-center text-white/75 text-sm py-4 text-contrast-soft">
                No comments yet. Be the first!
              </p>
            ) : (
              comments.map((comment, index) => (
                <div
                  key={comment.id}
                  className="photo-comment rounded-xl p-4"
                >
                  <p className="font-medium text-sm text-white text-contrast-soft">{comment.user_name}</p>
                  <p className="text-sm text-white/90 mt-1 text-contrast-soft">{comment.content}</p>
                  <p className="text-xs text-white/65 mt-2">
                    {new Date(comment.created_at).toLocaleDateString()}
                  </p>
                </div>
              ))
            )}
          </div>}

          </div>
          {/* Comment Form */}
          {commentsEnabled && <form onSubmit={handleComment} className="photo-comment-form space-y-3 rounded-xl p-4">
            <div className="text-xs text-white/75 mb-1 text-contrast-soft">
              {user
                ? <>Commenting as <span className="font-medium text-white">{user.name}</span></>
                : <span>Please <a href="/login" className="underline hover:text-white">log in</a> to comment</span>
              }
            </div>
            <div className="photo-comment-fields flex flex-wrap gap-2">
              <Textarea
                aria-label="Your comment" placeholder="Write a comment..."
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                className="min-h-[3.75rem] flex-1 resize-none rounded-xl text-white placeholder:text-white/50 focus:ring-2 focus:ring-white/30"
                style={{ background: 'rgba(255,255,255,0.06)', border: 0 }}
                data-testid="comment-content-input"
                disabled={!user}
              />
              <Button
                type="submit"
                size="icon"
                disabled={loading || !user}
                className="h-11 w-11 shrink-0 rounded-xl smooth-btn"
                aria-label="Post comment" data-testid="submit-comment"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </form>}
        </div>
      </div>
    </div>
    </DialogPrimitive.Content></DialogPrimitive.Portal></DialogPrimitive.Root>
  );
};

// ============== SITE FOOTER (borderless, on the snow bg) ==============

const SiteFooter = () => {
  return (
    <footer className="relative z-10 py-12 md:py-16">
      <div className="max-w-[1600px] mx-auto px-4 md:px-8 lg:px-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-8">
          <div className="max-w-md">
            <h3
              className="text-on-photo-strong text-2xl md:text-3xl font-light tracking-tight"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              Ferdia's Gallery
            </h3>
            <p className="text-on-photo-muted text-sm mt-2 leading-relaxed">
              Wildlife, landscapes, and the energy of sport — captured and curated.
            </p>
          </div>

          <div className="flex flex-col md:items-end gap-2">
            <p className="text-on-photo-muted text-xs uppercase tracking-[0.3em] mb-1">
              Enquiries
            </p>
            <a
              href="mailto:business.ferdiakavanagh@gmail.com"
              className="text-on-photo text-sm hover:text-[hsl(var(--accent))]"
            >
              business.ferdiakavanagh@gmail.com
            </a>
            <Link
              to="/privacy-policy"
              className="text-on-photo-muted text-sm hover:text-[hsl(var(--accent))]"
              data-testid="footer-privacy"
            >
              Privacy Policy
            </Link>
          </div>
        </div>

        <div className="mt-10 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-on-photo-muted text-xs">
            © {new Date().getFullYear()} Ferdia Kavanagh. All rights reserved.
          </p>
          <p className="text-on-photo-muted text-xs">
            All images are protected and may not be used without permission.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Gallery;
